import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/site/Reveal";
import { ArrowUpRight } from "lucide-react";
import { useBlogPosts } from "../lib/useBlog";
import { BLOG } from "../constants/testIds";
import { useSeo } from "../lib/seo";

export default function BlogIndex() {
  const { posts, loading } = useBlogPosts(50);

  useSeo({
    title:
      "The Alae Dental Journal. Patient Education and Dental Care Tips in Woodbridge",
    description:
      "Practical guidance on dental care, treatment, and healthy smiles from Alae Dental in Vaughan / Woodbridge. Written by a practice that believes in informed patients.",
    path: "/blog",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "The Alae Dental Journal",
      url: "https://www.alaedental.com/blog",
      description:
        "Practical guidance on dental care, treatment, and healthy smiles from Alae Dental.",
      author: {
        "@type": "Person",
        name: "Dr. Nausheen Mirza",
        url: "https://www.alaedental.com/about",
      },
    },
    jsonLdId: "blog-index-jsonld",
  });

  return (
    <main className="bg-linen text-umber" data-testid={BLOG.index}>
      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 bg-paper paper-grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-meta text-forest mb-6">The Journal</div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-umber max-w-4xl">
              From the <span className="font-display-italic">practice.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-umber/85 max-w-2xl">
              Practical guidance on caring for your smile, what to expect from
              treatment, and the thinking behind how we practice.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28 paper-grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {loading && (
            <p className="font-meta text-umber/60">Loading articles…</p>
          )}
          <div className="border-t border-umber/20">
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 70}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={BLOG.card(p.slug)}
                  className="group block border-b border-umber/20 py-10 md:py-12 transition-colors duration-300 hover:bg-paper/50"
                >
                  <div className="grid grid-cols-12 gap-6 items-start">
                    <div className="col-span-12 md:col-span-2 font-meta text-forest">
                      {new Date(p.published_at).toLocaleDateString("en-US", {
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                    <div className="col-span-12 md:col-span-7">
                      <h2 className="font-display text-3xl md:text-4xl leading-tight text-umber group-hover:text-forest transition-colors">
                        {p.title}
                      </h2>
                      <p className="mt-4 text-umber/80 leading-relaxed max-w-2xl">
                        {p.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.22em] uppercase text-forest">
                        Read article <ArrowUpRight size={14} />
                      </span>
                    </div>
                    <div className="hidden md:flex col-span-3 justify-end items-start pt-2">
                      <span className="font-meta text-umber/50">
                        {p.author}
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
